import { apiClient } from "../client";
import type { NdrResponseInput, OrderShipment } from "./dashboard";
import type { Envelope } from "./catalog";

/** Whether Shiprocket can deliver to a PIN, from the shop's pickup address. */
export interface DeliveryEstimate {
  pincode: string;
  serviceable: boolean;
  city: string | null;
  state: string | null;
  /** Fastest courier's expected delivery date, YYYY-MM-DD. */
  estimated_date: string | null;
  estimated_days: number | null;
  cod_available: boolean;
}

export interface DeliveryEstimateResponse {
  success: boolean;
  message: string;
  data: DeliveryEstimate;
}

/** Public tracking: an order's status and its (public-view) shipment, no items. */
export interface PublicTracking {
  order_number: string | null;
  order_status: string;
  ordered_at: string;
  /** Null until the order has an AWB. Only the destination city, never the address. */
  shipment: OrderShipment | null;
}

export const shipping = {
  /** GET /shipping/serviceability?pincode= — public; 422 bad PIN, 503 check unavailable. */
  serviceability: (pincode: string) =>
    apiClient.get<DeliveryEstimateResponse>("/shipping/serviceability", { params: { pincode } }),

  /**
   * POST /shipping/track — public, rate-limited (429). Either an order
   * number/AWB plus the order's email or phone, or the signed token from a
   * tracking link. Any miss, including a wrong contact, is the same 404.
   */
  track: (body: { query: string; contact: string } | { token: string }) =>
    apiClient.post<Envelope<PublicTracking>>("/shipping/track", body),

  /** GET /shipping/ndr/:token — the failed delivery behind an email/WhatsApp link. */
  ndrByToken: (token: string) => apiClient.get<Envelope<PublicTracking>>(`/shipping/ndr/${encodeURIComponent(token)}`),

  /** POST /shipping/ndr/:token — ask for another attempt without logging in. */
  ndrRespondByToken: (token: string, body: NdrResponseInput) =>
    apiClient.post<Envelope<{ submitted: boolean }>>(`/shipping/ndr/${encodeURIComponent(token)}`, body),
};
